import { ReactNode } from "react";
import { Button } from "@carbon/react";
import { Information, Edit, Close, SendAlt } from "@carbon/icons-react";
import {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import { FileReader } from "../../../components";
import {
  PurchaseRequestItemState,
  PurchaseRequestItemType,
  PurchaseRequestItemV2,
} from "../../../types";
import { currencyFormatter } from "../../../utils/utils";
import { TFunc } from "../purchaseRequestListTypes";
import { statusTag } from "../purchaseRequestRows";

/**
 * Header/row mappers for the detail screen's item `DataTable` (Task 5). The item STATE MACHINE
 * display reuses the shared `statusTag` (the same `purchaseRequests.status.*` label mapping the
 * pre-Carbon `Pill` used, now a colour-mapped Carbon `Tag`) — no completeness is re-derived here.
 * Cell values are display-ready nodes/strings, per the `DataTable` contract.
 */

export interface DetailItemRow extends DataTableRow {
  id: string;
  group: string;
  name: ReactNode;
  quantity: string;
  budget: ReactNode;
  buyer: string;
  status: ReactNode;
  actions: ReactNode;
}

export interface DetailItemColumnFlags {
  showBudgetPerItem: boolean;
  showActions: boolean;
  /** An imported file's requisitions live on its items; the column only appears when there are some. */
  showGroup?: boolean;
}

export const detailItemHeaders = (
  t: TFunc,
  { showBudgetPerItem, showActions, showGroup }: DetailItemColumnFlags
): DataTableColumn<DetailItemRow>[] => {
  const headers: DataTableColumn<DetailItemRow>[] = [];
  if (showGroup) {
    headers.push({ key: "group", header: t("purchaseRequests.items.columns.group") });
  }
  headers.push(
    { key: "name", header: t("purchaseRequests.items.title") },
    { key: "quantity", header: t("purchaseRequests.items.columns.quantity") }
  );
  if (showBudgetPerItem) {
    headers.push({
      key: "budget",
      header: t("purchaseRequests.items.columns.budgetValue"),
    });
  }
  headers.push({ key: "buyer", header: t("userTypes.buyer") });
  headers.push({ key: "status", header: t("purchaseRequests.items.columns.state") });
  if (showActions) {
    headers.push({ key: "actions", header: "" });
  }
  return headers;
};

export interface DetailItemRowCallbacks {
  onOpenInfo: (item: PurchaseRequestItemV2) => void;
  onEdit: (item: PurchaseRequestItemV2) => void;
  onReject: (item: PurchaseRequestItemV2) => void;
  /** Requester-side: flip a REJECTED item back to IN_APPROVAL and resend to the buyer. */
  onResend: (item: PurchaseRequestItemV2) => void;
  canEditRequest: boolean;
  canBuyerReject: boolean;
  isBusy: boolean;
}

export const mapItemToDetailRow = (
  item: PurchaseRequestItemV2,
  t: TFunc,
  cb: DetailItemRowCallbacks
): DetailItemRow => {
  const isDisabled = item.state !== PurchaseRequestItemState.IN_APPROVAL;

  const nameCell = (
    <span className="pr-detail__item-name">
      <span>{item.material?.ean || "-"}</span>
      <span>-</span>
      <span>{item.material?.globalMaterialName || "-"}</span>
      {item.itemType === PurchaseRequestItemType.SERVICE ? (
        <span className="pr-detail__item-type-tag">
          {t("purchaseRequests.items.columns.itemType")}: {item.itemType}
        </span>
      ) : null}
      {item.material ? (
        <button
          type="button"
          className="pr-detail__icon-button"
          onClick={() => cb.onOpenInfo(item)}
          aria-label={t("purchaseRequests.items.materialInfo")}
          title={t("purchaseRequests.items.materialInfo")}
        >
          <Information size={16} />
        </button>
      ) : null}
    </span>
  );

  const statusCell = (
    <div className="pr-detail__item-status">
      {statusTag(item.state, t)}
      {item.state === PurchaseRequestItemState.REJECTED && item.rejectedMessage ? (
        <p className="pr-detail__item-rejected">{item.rejectedMessage}</p>
      ) : null}
      {item.files && item.files.length > 0 ? (
        <div className="pr-detail__item-files">
          <p className="pr-detail__item-files-label">
            {t("purchaseRequests.itemFiles", {
              defaultValue: "Item attachments",
            })}
          </p>
          {item.files.map((file) => (
            <div className="pr-detail__item-file" key={file.id ?? file.fileURL}>
              <span>{file.name ?? file.fileURL}</span>
              <FileReader fileKey={file.fileURL} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  const isRejected = item.state === PurchaseRequestItemState.REJECTED;

  const actionsCell = (
    <div className="pr-detail__item-actions">
      {cb.canEditRequest ? (
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Edit}
          iconDescription={t("cta.edit")}
          onClick={() => cb.onEdit(item)}
          disabled={cb.isBusy}
        />
      ) : null}
      {cb.canEditRequest && isRejected ? (
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={SendAlt}
          iconDescription={t("purchaseRequests.items.resend")}
          onClick={() => cb.onResend(item)}
          disabled={cb.isBusy}
        />
      ) : null}
      {cb.canBuyerReject ? (
        <Button
          kind="danger--ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription={t("cta.reject")}
          onClick={() => cb.onReject(item)}
          disabled={cb.isBusy || isDisabled}
        />
      ) : null}
    </div>
  );

  return {
    id: item.id,
    group: item.groupLabel || "-",
    name: nameCell,
    quantity: `${item.quantity ?? ""} ${
      // `||`: a service's material has an empty unit, the item's own is the real one.
      item.material?.measurementUnit || item.measurementUnit || ""
    }`.trim(),
    budget:
      item.budgetValue != null
        ? currencyFormatter(item.budgetValue, item.currency || undefined)
        : "-",
    buyer: item.material?.owner?.fullName || "-",
    status: statusCell,
    actions: actionsCell,
  };
};
